import { Fragment, ReactNode } from 'react';

import { Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps, SitecoreContextValue } from 'src/lib/ComponentProps';

import { ExtendedItem } from 'src/types/ExtendedItem';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { EditorHelp } from 'src/components/layout/_EditorHelp/_EditorHelp';

import CalendarFilledIconSvg from 'public/icons/CalendarFilled.svg';

import ContentOverviewHeaderContainer from '../../contentOverview/_contentOverviewHeaderContainer/ContentOverviewHeaderContainer';
import ContentOverviewActionButton from '../../contentOverview/contentOverviewActionButton/ContentOverviewActionButton';
import { CourseFields } from '../CourseFields';

import styles from './CourseOverviewCohorts.module.css';

export type Cohort = {
	id: string;
	fields: {
		Name: Field<string>;
		Delivery: ExtendedItem & {
			fields: {
				Title: Field<string>;
			};
		};
		Description: Field<string>;
		ShowRegisterInterestButton: Field<boolean>;
		SessionDates: Field<string>;
		SessionPrefix: Field<string>;
	};
};

type CourseOverviewCohortsProps = ComponentProps & {
	fields: CourseFields & {
		items: Cohort[];
	};
};

export default function CourseOverviewCohorts(props: CourseOverviewCohortsProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const isEditing = IsSitecoreEditing();

	if (isEditing && (!props.fields?.items || props.fields?.items.length === 0)) {
		return (
			<section className="maxWidth padX padYL">
				<EditorHelp
					text={
						'To display Cohorts for this course, view this course in the Content Editor and create a Cohort in the cohort folder underneath.'
					}
					indent
				/>
			</section>
		);
	}

	const formatDate = (dateString: string) => {
		if (!dateString) {
			return;
		}

		const dateObject = new Date(dateString);

		return dateObject
			.toLocaleDateString('en-GB', {
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
			.replace(',', '');
	};

	function filterPastCohorts(cohort: Cohort): boolean {
		if (!cohort) {
			return false;
		}
		let allDatesExpired = true;

		const sessionDateStrings: string[] = cohort.fields?.SessionDates?.value?.trim().split(',');
		sessionDateStrings?.forEach((dateString) => {
			const date = new Date(dateString);
			if (date >= new Date()) {
				allDatesExpired = false;
			}
		});

		return !allDatesExpired;
	}

	function displayDates(cohort: Cohort): ReactNode {
		return cohort.fields?.SessionDates?.value
			.trim()
			.split(',')
			.map((sessionDate, index) => (
				<Fragment key={sessionDate}>
					<Text className={styles.cohortDay} tag="p" field={{ value: `${cohort?.fields?.SessionPrefix?.value} ${index + 1}:` }} />
					<Text className={styles.cohortSessionDate} tag="p" field={{ value: formatDate(sessionDate) }} />
				</Fragment>
			));
	}

	const cohorts = props.fields?.items.filter((cohort: Cohort) => filterPastCohorts(cohort))?.filter((cohort) => cohort);

	if (!isEditing && (!cohorts || cohorts.length === 0)) {
		return <></>;
	}

	return (
		<section className="maxWidth padX padYL" data-display-pdf-download>
			<ContentOverviewHeaderContainer header={sitecoreContext.dictionary.upcomingCourseDateHeading} />
			<div className={styles.cohortGrid}>
				{cohorts?.map((cohort: Cohort) => (
					<div className={styles.cohortCard} key={cohort.id}>
						<div className={styles.cohortCardContent}>
							<span className={styles.cohortName}>
								<CalendarFilledIconSvg className={styles.cohortIcon} />
								<Text field={cohort.fields?.Name} />
							</span>
							<Text tag="p" field={cohort.fields?.Delivery?.fields?.Title} className={styles.cohortDelivery} />
							<Text tag="p" field={cohort.fields.Description} />
							{displayDates(cohort)}
						</div>
						<div className={styles.cohortButtonContainer}>
							<ContentOverviewActionButton registerYourInterestOnly removePadding {...props} />
						</div>
					</div>
				))}
			</div>
		</section>
	);
}
